import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { GridLogicOperator } from '@mui/x-data-grid-pro';
import { ServerDataGrid } from '@warehouse/shared/ui';
import { brokerServicesRepositorySingleton } from '@warehouse/object-browser/feature-broker';
import { NoRowsOverlay } from '@warehouse/object-browser/feature-object-browser';
import { BrokerJob } from '@warehouse/object-browser/feature-broker-core';
import { ServiceLogStore, serviceLogStoreSelector, useServiceLogStore } from '@warehouse/service-log/domain';
import { ServiceLogColumnField } from '@warehouse/service-log/core';
import { createServiceLogColumns } from './columns';
import { serviceLogGridConfig } from './service-log.grid-config';

/** Fills the card so the grid scrolls inside it and the pagination footer stays visible. */
const TableWrapper = styled('div')({
	display: 'flex',
	flex: 1,
	flexDirection: 'column',
	minHeight: 0,
});

/** The Object Browser's empty-grid overlay, so both full-page grids look the same when empty. */
function NoJobsFoundOverlay() {
	return <NoRowsOverlay text="No jobs found" />;
}

interface ServiceLogGridProps {
	/**
	 * @deprecated The method should only be used for component tests
	 */
	disableVirtualization?: boolean;
}

/** The Object Browser's ServerDataGrid over the tenant's Broker jobs. */
export function ServiceLogGrid({ disableVirtualization }: ServiceLogGridProps) {
	const jobs = useServiceLogStore(serviceLogStoreSelector.jobs);
	const isLoading = useServiceLogStore(serviceLogStoreSelector.isLoading);
	const { refresh } = useServiceLogStore(serviceLogStoreSelector.actions);

	const onJobCancelled = useCallback(() => {
		refresh();
	}, [refresh]);

	const columns = useMemo(
		() =>
			createServiceLogColumns({
				services: brokerServicesRepositorySingleton
					.get()
					.getAvailableServicesConfig()
					.filter((service) => service.active),
				onJobCancelled,
			}),
		[onJobCancelled],
	);

	return (
		<TableWrapper>
			<ServerDataGrid<BrokerJob, ServiceLogColumnField, ServiceLogStore>
				$variant="full"
				columns={columns}
				rows={jobs}
				useStore={useServiceLogStore}
				selector={serviceLogStoreSelector}
				config={serviceLogGridConfig}
				// Job ids are content-addressed and repeat across submissions; the Logs tab keys rows the same way.
				getRowId={(row) => `${row.jobId}:${row.startedAt}`}
				loading={isLoading}
				rowSelection={false}
				disableRowSelectionOnClick
				slots={{ noRowsOverlay: NoJobsFoundOverlay }}
				// The Broker only ANDs conditions (and has no OR across fields), so the panel does not offer "Or".
				slotProps={{ filterPanel: { logicOperators: [GridLogicOperator.And] } }}
				disableVirtualization={disableVirtualization}
			/>
		</TableWrapper>
	);
}
